'use client';

import React, { useId } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { BronzeBadge, GoldBadge, RankedBadge, SilverBadge } from '@/components/Icons';
import { Exchange } from '@/types/exchanges/page';
import { toPersianDigits } from '@/utils/persianUtils';

interface ExchangesListCardProps {
    brokers?: Exchange[];
    onStarClick?: (broker: Exchange) => void | undefined;
    lastModifiedTitle?: string;
    currentPage?: number;
    isRanked?: boolean;
    excludeItem?: Exchange[] | undefined;
    isLanding?: boolean;
    // Accepted for older callers; badge ids are now unique on their own (see Icons.tsx).
    idPath?: string;
    visibleCount?: number;
    useCssHiding?: boolean;
    adsCount?: number;
}

// The check marks, star, comment and «open» icons repeat on every card (40+ cards, ~3.5 KB of
// path data each). They are drawn once per list here and each card points at them with <use>.
// Own sprite (not the layout's SvgSprite): the list is also used on landings without that layout.
const CHECK_PATH =
    'M476.594 205.81a86.5 86.5 0 0 1-17.64-15.732 80.804 80.804 0 0 1 4.999-24.542c5.511-18.656 12.374-41.873-.853-60.041-13.323-18.313-37.661-18.927-57.214-19.427a83.836 83.836 0 0 1-24.421-2.626 82.981 82.981 0 0 1-9.927-22.197c-6.524-18.58-14.652-41.706-36.417-48.779-21.12-6.864-40.074 6.177-56.787 17.645a79.577 79.577 0 0 1-22.328 12.553 79.45 79.45 0 0 1-22.338-12.553c-16.713-11.478-35.672-24.478-56.787-17.645-21.76 7.073-29.885 30.187-36.416 48.771a84.607 84.607 0 0 1-9.823 22.125 81.948 81.948 0 0 1-24.527 2.698c-19.552.5-43.89 1.115-57.213 19.426-13.227 18.178-6.365 41.396-.854 60.054a83.096 83.096 0 0 1 5.047 24.386 83.502 83.502 0 0 1-17.692 15.885C19.627 217.842 0 232.82 0 255.997c0 23.178 19.627 38.156 35.406 50.188a86.51 86.51 0 0 1 17.64 15.732 80.814 80.814 0 0 1-5 24.542c-5.51 18.655-12.373 41.875.854 60.041 13.323 18.313 37.66 18.927 57.214 19.428a83.83 83.83 0 0 1 24.421 2.625 82.981 82.981 0 0 1 9.927 22.198c6.532 18.583 14.657 41.706 36.422 48.781a38.322 38.322 0 0 0 11.947 1.907c16.38 0 31.338-10.282 44.839-19.552a79.573 79.573 0 0 1 22.329-12.556 79.469 79.469 0 0 1 22.34 12.552c16.712 11.479 35.666 24.469 56.786 17.646 21.76-7.073 29.885-30.187 36.416-48.772a84.612 84.612 0 0 1 9.823-22.124 81.944 81.944 0 0 1 24.527-2.698c19.552-.499 43.891-1.115 57.213-19.426 13.227-18.177 6.365-41.397.854-60.054a83.093 83.093 0 0 1-5.047-24.386 83.484 83.484 0 0 1 17.692-15.885c15.769-12.031 35.396-27.009 35.396-50.187.001-23.177-19.626-38.155-35.405-50.187Zm-125.51 11.938L244.417 324.414c-8.328 8.331-21.832 8.333-30.163.005l-.005-.005-53.334-53.333c-8.436-8.225-8.607-21.732-.383-30.168 8.225-8.437 21.732-8.608 30.168-.383.129.126.257.253.383.383l38.25 38.25 91.583-91.584c8.225-8.436 21.731-8.607 30.168-.382 8.436 8.225 8.607 21.731.382 30.168a23 23 0 0 1-.382.383Z';
const STAR_PATH =
    'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z';

const CardSprite: React.FC<{ prefix: string }> = ({ prefix }) => (
    <svg
        aria-hidden='true'
        focusable='false'
        width='0'
        height='0'
        style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
        <defs>
            <symbol id={`${prefix}-blue-check`} viewBox='0 0 512 512'>
                <path fill='#fff' d='M147 154h218v222H147z' />
                <path fill='#3998F1' d={CHECK_PATH} />
            </symbol>
            <symbol id={`${prefix}-gold-check`} viewBox='0 0 512 512'>
                <path fill='#fff' d='M147 143h218v222H147z' />
                <path fill='#E7BE1E' d={CHECK_PATH} />
            </symbol>
            <symbol id={`${prefix}-star`} viewBox='0 0 24 24'>
                <path d={STAR_PATH} />
            </symbol>
            <symbol id={`${prefix}-message`} viewBox='0 0 24 24' fill='none'>
                <path
                    d='M8.5 19H8c-4 0-6-1-6-6V8c0-4 2-6 6-6h8c4 0 6 2 6 6v5c0 4-2 6-6 6h-.5c-.31 0-.61.15-.8.4l-1.5 2c-.66.88-1.74.88-2.4 0l-1.5-2c-.16-.22-.53-.4-.8-.4Z'
                    strokeWidth='1.5'
                    strokeMiterlimit='10'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
                <path
                    d='M7 8h10M7 13h6'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                />
            </symbol>
            <symbol id={`${prefix}-export`} viewBox='0 0 24 24' fill='none'>
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='1.5'
                    d='M13 11l8.2-8.2M22 6.8V2h-4.8M11 2H9C4 2 2 4 2 9v6c0 5 2 7 7 7h6c5 0 7-2 7-7v-2'
                />
            </symbol>
        </defs>
    </svg>
);

const UseIcon: React.FC<{ href: string; size: number; className?: string; star?: boolean }> = ({
    href,
    size,
    className,
    star,
}) => (
    <svg
        aria-hidden='true'
        width={size}
        height={size}
        className={className}
        {...(star
            ? {
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 1.5,
                  strokeLinecap: 'round' as const,
                  strokeLinejoin: 'round' as const,
              }
            : {})}
    >
        <use href={href} />
    </svg>
);

export const getBadge = (
    number: number,
    isRank: boolean = false,
    size?: string,
): React.JSX.Element => {
    const adjustedNumber = isRank ? number - 1 : number;
    switch (adjustedNumber) {
        case 0:
            return <GoldBadge className={size ? size : 'size-8'} />;
        case 1:
            return <SilverBadge className={size ? size : 'size-8'} />;
        case 2:
            return <BronzeBadge className={size ? size : 'size-8'} />;
        default:
            return (
                <div className='text-base text-primary/70 dark:text-white/70 font-light'>
                    {isRank ? number : adjustedNumber + 1}
                </div>
            );
    }
};

const ExchangesListCard: React.FC<ExchangesListCardProps> = ({
    brokers,
    onStarClick,
    lastModifiedTitle,
    currentPage = 1,
    isRanked,
    excludeItem,
    isLanding,
    visibleCount,
    useCssHiding = false,
    adsCount = 0,
}) => {
    const getPaginatedRank = (index: number): number => {
        const adjustedIndex = index - adsCount;
        return (currentPage - 1) * 10 + adjustedIndex + 1;
    };

    // useId keeps the sprite ids unique when a page renders more than one list (landing tabs).
    const spriteId = `kslc${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
    const icon = (name: string) => `#${spriteId}-${name}`;

    const filteredBrokers = brokers?.filter(
        (broker) => !excludeItem?.some((excluded) => excluded.databaseId === broker.databaseId),
    );

    return (
        <>
            <CardSprite prefix={spriteId} />
            <ul className='grid grid-cols-1 lg:justify-center'>
                {filteredBrokers?.map((broker, index) => {
                    // Determine visibility based on the count prop
                    const isHidden =
                        useCssHiding && visibleCount !== undefined && index >= visibleCount;

                    // --- LOGIC: Identify if this item is an Ad ---
                    const isAd = index < adsCount;

                    // --- LOGIC: Badge Content ---
                    let badgeContent: React.ReactNode;

                    if (isAd) {
                        badgeContent = (
                            <div className='flex flex-col items-center justify-center gap-1'>
                                <div className='w-6 h-6 text-yellow-500'>
                                    <RankedBadge className='size-8' />
                                </div>
                            </div>
                        );
                    } else {
                        badgeContent = isRanked ? (
                            <RankedBadge className='size-8' />
                        ) : (
                            getBadge(getPaginatedRank(index), true)
                        );
                    }

                    const {
                        faName,
                        enName,
                        logo,
                        ctaButton,
                        ctaId,
                        moreButtonId,
                        sponsored,
                        verified,
                        badge,
                    } = broker?.brokerSingle?.newfields;
                    const href = isLanding
                        ? ctaButton?.url || broker?.uri || '#'
                        : broker?.uri || '#';

                    const finalRating =
                        lastModifiedTitle === 'broker'
                            ? broker?.brokerFields?.generalInfo?.brokerRating?.finalRating
                            : lastModifiedTitle === 'gold'
                              ? broker?.goldFields?.generalInfo?.brokerRating?.finalRating
                              : broker?.exchangeFields?.generalInfo?.brokerRating?.finalRating || 0;

                    return (
                        <li
                            key={broker.databaseId}
                            // SEO FIX: We render the full content, but toggle the class between 'flex' and 'hidden'.
                            // 'hidden' applies display:none, but the HTML nodes remain in the source for Google to crawl.
                            className={`relative justify-between hover:bg-primary/5 dark:hover:bg-white/5 overflow-hidden border-b p-1 border-primary/10 dark:border-white/20 ${
                                isHidden ? 'hidden' : 'flex'
                            }`}
                        >
                            <div className='w-16 flex justify-center text-2xl'>
                                <div className='flex flex-col justify-center items-center gap-3'>
                                    {badgeContent}
                                </div>
                            </div>

                            <div className='w-full border-r border-primary/10 dark:border-white/20 lg:pe-3 ps-3 flex flex-col lg:flex-row justify-center lg:justify-between text-white lg:items-center max-lg:gap-1 max-lg:py-1 gap-3'>
                                <Link
                                    href={href}
                                    id={moreButtonId || ''}
                                    target={'_blank'}
                                    className='flex gap-4 items-center'
                                >
                                    {logo?.mediaItemUrl ? (
                                        <Image
                                            src={logo?.mediaItemUrl}
                                            alt={enName}
                                            className='object-contain size-8'
                                            // eager without a <head> preload: 4 tiny logos must not
                                            // compete with the page's own LCP. Drawn at 32 px, so a
                                            // 48/96 px file (was 128/256).
                                            loading={index < 4 ? 'eager' : 'lazy'}
                                            title={enName}
                                            width={48}
                                            height={48}
                                        />
                                    ) : (
                                        // Fallback UI if logo is missing (Optional: You can use a placeholder icon here)
                                        <div className='size-8 rounded-none bg-gray-100 flex items-center justify-center text-xs text-gray-400'>
                                            Logo
                                        </div>
                                    )}
                                    <div className='flex flex-col items-start dark:text-white lg:text-xl text-primary'>
                                        <div className='inline-flex items-center gap-2  max-lg:gap-1 w-full'>
                                            <span className='max-md:text-sm text-base whitespace-nowrap'>
                                                {faName}
                                            </span>
                                            <span className='text-xs dark:text-white/80 max-lg:text-xxs line-clamp-1'>
                                                ({enName})
                                            </span>
                                            {sponsored ? (
                                                <div className='w-4'>
                                                    <UseIcon
                                                        href={icon('gold-check')}
                                                        size={16}
                                                        className='w-full h-full'
                                                    />
                                                </div>
                                            ) : verified || badge ? (
                                                <div className='w-4'>
                                                    <UseIcon
                                                        href={icon('blue-check')}
                                                        size={16}
                                                        className='w-full h-full'
                                                    />
                                                </div>
                                            ) : null}
                                        </div>

                                        <div className='flex items-center gap-2 text-primary/70 dark:text-white/70'>
                                            <div className='text-xs flex gap-1 items-center'>
                                                <UseIcon
                                                    href={icon('message')}
                                                    size={18}
                                                    className='stroke-primary/40 dark:stroke-white'
                                                />
                                                {toPersianDigits(broker?.postRatings?.count)}
                                                <span>نظر</span>
                                            </div>
                                            .
                                            <div
                                                className='flex items-center cursor-pointer gap-1'
                                                onClick={() => onStarClick?.(broker)}
                                            >
                                                <div className='relative w-[18px] h-[18px]'>
                                                    <div
                                                        className='absolute top-0 left-0 right-0 w-full h-full overflow-hidden'
                                                        style={{
                                                            width: `${(Number(finalRating || 0) / 5) * 100}%`,
                                                        }}
                                                    >
                                                        <UseIcon
                                                            star
                                                            href={icon('star')}
                                                            size={20}
                                                            className='fill-yellow-500 stroke-yellow-500'
                                                        />
                                                    </div>
                                                    <div className='absolute top-0 left-0 w-full h-full'>
                                                        <UseIcon
                                                            star
                                                            href={icon('star')}
                                                            size={20}
                                                            className='stroke-yellow-500'
                                                        />
                                                    </div>
                                                </div>
                                                <span className='text-xs'>
                                                    {toPersianDigits(finalRating)} از ۵ امتیاز
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>

                                <Link
                                    href={ctaButton?.url || broker?.uri || '#'}
                                    target='_blank'
                                    rel='nofollow'
                                    id={ctaId || ''}
                                    className='bg-progress hover:brightness-110 justify-center max-lg:py-3 text-center flex text-nowrap items-center gap-2 p-2 text-xs lg:px-3 lg:rounded-xl rounded-2xl'
                                >
                                    <UseIcon
                                        href={icon('export')}
                                        size={15}
                                        className='stroke-white stroke-2'
                                    />
                                    {ctaButton?.title || 'افتتاح حساب'}
                                </Link>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </>
    );
};

export default ExchangesListCard;
